import React, { useState } from "react";
import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    sendEmailVerification,
    GoogleAuthProvider,
    signInWithPopup
} from "firebase/auth";
import { auth, db } from "../firebase/firebaseConfig";
import { collection, addDoc, serverTimestamp, doc, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { getUserProfile } from "../services/userService";

/* ─── Palette ──────────────────────────────────────────────────────────────
   red-600  → #DC2626   red-700  → #B91C1C
   blue-900 → #1E3A8A   blue-800 → #1E40AF
   off-white → #F8F7F4   text     → #111827
 ───────────────────────────────────────────────────────────────────────────── */

const LandingPage = () => {
    const navigate = useNavigate();

    // Auth tab
    const [authTab, setAuthTab] = useState("login"); // "login" | "register"

    // Login state
    const [loginEmail, setLoginEmail] = useState("");
    const [loginPassword, setLoginPassword] = useState("");
    const [loginLoading, setLoginLoading] = useState(false);
    const [loginError, setLoginError] = useState("");

    // Register state
    const [regName, setRegName] = useState("");
    const [regEmail, setRegEmail] = useState("");
    const [regPassword, setRegPassword] = useState("");
    const [regLoading, setRegLoading] = useState(false);
    const [regError, setRegError] = useState("");
    const [regSuccess, setRegSuccess] = useState(false);

    // Contact state
    const [contact, setContact] = useState({ name: "", email: "", phone: "", message: "" });
    const [contactLoading, setContactLoading] = useState(false);
    const [contactSuccess, setContactSuccess] = useState(false);
    const [contactError, setContactError] = useState("");

    // Forgot Password & OTP States
    const [showForgotModal, setShowForgotModal] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotError, setForgotError] = useState("");
    const [forgotSuccess, setForgotSuccess] = useState("");

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError("");
        try {
            setLoginLoading(true);

            // Admin auto-provisioning via Landing Page
            if (loginEmail.trim().toLowerCase() === "admin@bookplatform.com" && loginPassword === "AdminPassword123!") {
                let user;
                try {
                    const cred = await signInWithEmailAndPassword(auth, "admin@bookplatform.com", "AdminPassword123!");
                    user = cred.user;
                } catch (signInErr) {
                    if (signInErr.code === "auth/user-not-found" || signInErr.code === "auth/invalid-credential") {
                        const cred = await createUserWithEmailAndPassword(auth, "admin@bookplatform.com", "AdminPassword123!");
                        user = cred.user;
                    } else {
                        throw signInErr;
                    }
                }
                if (user) {
                    await setDoc(doc(db, "users", user.uid), {
                        name: "System Administrator",
                        email: "admin@bookplatform.com",
                        role: "admin",
                        createdAt: serverTimestamp()
                    }, { merge: true });
                    navigate("/admin/dashboard");
                    return;
                }
            }

            const userCredential = await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
            const user = userCredential.user;

            // Enforce email verification (excluding admin)
            if (!user.emailVerified && user.email !== "admin@bookplatform.com") {
                await auth.signOut();
                setLoginError("Please verify your email address before logging in. A verification link was sent to your email.");
                return;
            }

            const profile = await getUserProfile(user.uid);
            if (profile && profile.role === "admin") {
                navigate("/admin/dashboard");
            } else {
                navigate("/dashboard");
            }
        } catch (err) {
            setLoginError(err.message.replace("Firebase: ", "").replace(/\(auth.*\)\.?/, ""));
        } finally {
            setLoginLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setRegError("");
        setRegSuccess(false);
        try {
            setRegLoading(true);
            const userCredential = await createUserWithEmailAndPassword(auth, regEmail, regPassword);
            const user = userCredential.user;

            // Trigger Firebase email verification
            await sendEmailVerification(user);

            // Create user profile in Firestore
            await setDoc(doc(db, "users", user.uid), {
                name: regName,
                email: regEmail,
                role: "author",
                createdAt: serverTimestamp()
            });

            // Sign out the new user immediately so they must verify & log in manually
            await auth.signOut();

            setRegSuccess(true);
            setRegError("Account created! A verification link has been sent to your email. Please verify before signing in.");

            // Switch to login tab after brief delay
            setTimeout(() => {
                setAuthTab("login");
                setRegSuccess(false);
                setRegError("");
            }, 5000);
        } catch (err) {
            setRegError(err.message.replace("Firebase: ", "").replace(/\(auth.*\)\.?/, ""));
        } finally {
            setRegLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setLoginError("");
        setRegError("");
        try {
            setLoginLoading(true);
            const provider = new GoogleAuthProvider();
            const result = await signInWithPopup(auth, provider);
            const user = result.user;

            // Check if profile document exists in Firestore
            let profile = await getUserProfile(user.uid);
            if (!profile) {
                // Auto-create Firestore profile document
                await setDoc(doc(db, "users", user.uid), {
                    name: user.displayName || "Google User",
                    email: user.email,
                    role: "author",
                    createdAt: serverTimestamp()
                });
                profile = { role: "author" };
            }

            if (profile && profile.role === "admin") {
                navigate("/admin/dashboard");
            } else {
                navigate("/dashboard");
            }
        } catch (err) {
            console.error("Google authentication failed:", err);
            setLoginError(err.message.replace("Firebase: ", "").replace(/\(auth.*\)\.?/, ""));
        } finally {
            setLoginLoading(false);
        }
    };

    const handleSendResetLink = async (e) => {
        e.preventDefault();
        setForgotError("");
        setForgotSuccess("");
        setForgotLoading(true);
        try {
            await sendPasswordResetEmail(auth, forgotEmail);
            setForgotSuccess("A secure password reset link has been successfully sent to your email address! Please check your inbox (and spam folder) to reset your password.");
        } catch (err) {
            setForgotError(err.message.replace("Firebase: ", "").replace(/\(auth.*\)\.?/, ""));
        } finally {
            setForgotLoading(false);
        }
    };

    const handleContact = async (e) => {
        e.preventDefault();
        setContactError("");
        try {
            setContactLoading(true);
            await addDoc(collection(db, "contactSubmissions"), {
                ...contact,
                createdAt: serverTimestamp(),
            });
            setContactSuccess(true);
            setContact({ name: "", email: "", phone: "", message: "" });
        } catch (err) {
            console.error("Failed to send contact submission:", err);
            setContactError("Failed to send. Please try again.");
        } finally {
            setContactLoading(false);
        }
    };

    const features = [
        {
            icon: "📖",
            title: "Publish Your Story",
            desc: "Upload your manuscript and our platform handles formatting, layout, and print-ready PDF generation automatically.",
        },
        {
            icon: "🎨",
            title: "AI Cover Design",
            desc: "Generate stunning book covers tailored to your genre and title — no design skills needed.",
        },
        {
            icon: "🌍",
            title: "Global Distribution",
            desc: "Reach readers worldwide with our distribution network supporting major digital formats."
        },
        {
            icon: "📖",
            title: "Journal Publication",
            desc: "World-class journal publishing services to showcase your research to the world."
        }
    ];

    return (
        <div className="min-h-screen bg-[#F8F7F4] font-sans">

            {/* ── NAV ─────────────────────────────────────────────────────── */}
            <nav className="sticky top-0 z-[100] flex items-center justify-between h-[72px] bg-white px-6 md:px-10 shadow-[0_2px_12px_rgba(0,0,0,0.08)] border-b border-[#F0F0F0]">
                <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 bg-[#DC2626] rounded-lg flex items-center justify-center text-lg">📚</div>
                    <span className="text-[#1E3A8A] font-extrabold text-xl tracking-tight">
                        Mrida<span className="text-[#DC2626]">Pub</span>
                    </span>
                </div>

                <div className="hidden md:flex gap-8 items-center">
                    {["Features", "How It Works", "Contact"].map(item => (
                        <a key={item} href={`#${item.toLowerCase().replace(" ", "-")}`}
                            className="text-gray-600 hover:text-[#1E3A8A] text-sm font-medium transition-colors duration-150 no-underline"
                        >{item}</a>
                    ))}
                </div>

                <div className="flex gap-3">
                    <button
                        onClick={() => {
                            document.querySelector('.auth-card')?.scrollIntoView({ behavior: 'smooth' });
                            setAuthTab("login");
                        }}
                        className="px-5 py-2 bg-transparent hover:bg-gray-100 text-[#1E3A8A] border-[1.5px] border-[#1E3A8A] rounded-lg text-xs font-semibold cursor-pointer transition-all duration-150"
                    >
                        Log In
                    </button>
                    <button
                        onClick={() => {
                            document.querySelector('.auth-card')?.scrollIntoView({ behavior: 'smooth' });
                            setAuthTab("register");
                        }}
                        className="px-5 py-2 bg-gradient-to-br from-[#DC2626] to-[#B91C1C] text-white rounded-lg text-xs font-semibold cursor-pointer transition-all duration-150 shadow-[0_2px_8px_rgba(220,38,38,0.25)] hover:-translate-y-[1px] hover:shadow-[0_4px_12px_rgba(220,38,38,0.35)]"
                    >
                        Get Started
                    </button>
                </div>
            </nav>

            {/* ── HERO + AUTH ──────────────────────────────────────────────── */}
            <section className="relative bg-gradient-to-br from-[#1E3A8A] via-[#1E40AF] to-[#1E3A8A] min-h-[calc(100vh-72px)] flex flex-col lg:flex-row items-center p-6 md:p-10 lg:p-16 gap-10 lg:gap-[60px] overflow-hidden">
                {/* Decorative circles */}
                <div className="absolute top-[-80px] left-[-80px] w-[360px] h-[360px] rounded-full bg-[#DC2626]/8 pointer-events-none" />
                <div className="absolute bottom-[-60px] left-[30%] w-[280px] h-[280px] rounded-full bg-white/5 pointer-events-none" />

                {/* Left: Hero copy */}
                <div className="relative z-10 flex-1 max-w-[560px]">
                    <div className="inline-flex items-center gap-2 bg-[#DC2626]/15 border border-[#DC2626]/30 rounded-full px-4 py-1.5 mb-7">
                        <span className="w-1.5 h-1.5 bg-[#DC2626] rounded-full inline-block" />
                        <span className="text-[#FCA5A5] text-xs font-bold tracking-wider uppercase">
                            India's Book Publishing Platform
                        </span>
                    </div>

                    <h1 className="text-white text-4xl md:text-5xl lg:text-[58px] font-extrabold leading-[1.1] mb-6 tracking-tight">
                        Your Book.<br />
                        <span className="text-[#DC2626]">Your Voice.</span><br />
                        The World's Stage.
                    </h1>

                    <p className="text-white/70 text-base md:text-lg leading-relaxed mb-10 max-w-[480px]">
                        MridaPub gives every author — first-time or seasoned — the tools to publish professionally. Design covers, format manuscripts, and distribute globally. All in one place.
                    </p>

                    {/* Trust badges */}
                    <div className="flex gap-8 flex-wrap">
                        {[["500+", "Books Published"], ["98%", "Author Satisfaction"], ["40+", "Countries Reached"]].map(([num, label]) => (
                            <div key={label}>
                                <div className="text-[#DC2626] text-2xl font-extrabold leading-none">{num}</div>
                                <div className="text-white/55 text-xs font-medium mt-0.5">{label}</div>
                            </div>
                        ))}
                    </div>

                    {/* Steps */}
                    <div className="mt-12 flex flex-col gap-4">
                        {[
                            "Register your free author account",
                            "Upload manuscript & generate your cover",
                            "Publish and sell to the world",
                        ].map((step, i) => (
                            <div key={i} className="flex items-center gap-3.5">
                                <div className="w-7 h-7 bg-[#DC2626] text-white rounded-full text-xs font-extrabold flex items-center justify-center shrink-0">{i + 1}</div>
                                <span className="text-white/75 text-sm">{step}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right: Auth Card */}
                <div className="auth-card w-full max-w-[420px] shrink-0 bg-white rounded-3xl shadow-[0_24px_80px_rgba(0,0,0,0.35)] overflow-hidden relative z-10">
                    {/* Red accent strip */}
                    <div className="h-1 bg-gradient-to-r from-[#DC2626] to-[#B91C1C]" />

                    {/* Tabs */}
                    <div className="flex border-b border-[#F3F4F6]">
                        {["login", "register"].map(tab => (
                            <button
                                key={tab}
                                onClick={() => setAuthTab(tab)}
                                className={`flex-1 py-4 border-none text-center font-bold text-sm tracking-wider uppercase cursor-pointer transition-all duration-150 ${authTab === tab ? "bg-white text-[#1E3A8A] border-b-2 border-[#DC2626]" : "bg-[#F9FAFB] text-[#9CA3AF] border-b-2 border-transparent"}`}
                            >
                                {tab === "login" ? "Sign In" : "Create Account"}
                            </button>
                        ))}
                    </div>

                    <div className="p-8">
                        {authTab === "login" ? (
                            <>
                                <h2 className="text-[#1E3A8A] text-2xl font-extrabold mb-1.5">Welcome back</h2>
                                <p className="text-gray-500 text-[13px] mb-7">Sign in to continue publishing</p>

                                {loginError && (
                                    <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3.5 py-2.5 mb-4 text-[#DC2626] text-xs">
                                        {loginError}
                                    </div>
                                )}

                                <form onSubmit={handleLogin} className="flex flex-col gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 mb-1.5 tracking-wider">Email address</label>
                                        <input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)}
                                            placeholder="you@example.com" required className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-200 focus:border-[#1E3A8A] rounded-lg text-sm text-gray-900 outline-none transition-colors bg-[#F9FAFB]"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 mb-1.5 tracking-wider">Password</label>
                                        <input type="password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)}
                                            placeholder="••••••••" required className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-200 focus:border-[#1E3A8A] rounded-lg text-sm text-gray-900 outline-none transition-colors bg-[#F9FAFB]"
                                        />
                                    </div>
                                    <div className="flex justify-end -mt-2">
                                        <span
                                            onClick={() => {
                                                setShowForgotModal(true);
                                                setForgotError("");
                                                setForgotSuccess("");
                                                setForgotEmail(loginEmail);
                                            }}
                                            className="text-xs text-[#2563EB] cursor-pointer font-semibold"
                                        >
                                            Forgot Password?
                                        </span>
                                    </div>
                                    <button type="submit" disabled={loginLoading} className={`w-full py-3.5 text-white rounded-xl text-sm font-bold tracking-wider transition-all duration-150 cursor-pointer ${loginLoading ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] hover:opacity-95"}`}>
                                        {loginLoading ? "Signing in…" : "Sign In →"}
                                    </button>
                                </form>

                                <div className="flex items-center gap-2 my-4">
                                    <div className="flex-1 h-[1px] bg-gray-200" />
                                    <span className="text-[11px] text-gray-400 uppercase font-bold">Or</span>
                                    <div className="flex-1 h-[1px] bg-gray-200" />
                                </div>
                                <button
                                    type="button"
                                    onClick={handleGoogleLogin}
                                    className="w-full flex items-center justify-center gap-2.5 border border-gray-300 rounded-lg py-2.5 px-4 bg-white hover:bg-gray-50 text-gray-700 text-xs font-semibold cursor-pointer transition-colors"
                                >
                                    <svg width="18" height="18" viewBox="0 0 18 18">
                                        <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4" />
                                        <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853" />
                                        <path d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.96H.957C.347 6.173 0 7.549 0 9s.347 2.827.957 4.04l3.007-2.333z" fill="#FBBC05" />
                                        <path d="M9 3.58c1.32 0 2.507.454 3.44 1.347l2.58-2.58C13.463.89 11.426 0 9 0 5.482 0 2.438 2.017.957 4.96L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335" />
                                    </svg>
                                    Continue with Google
                                </button>

                                <p className="text-center mt-5 text-[13px] text-gray-500">
                                    No account?{" "}
                                    <span onClick={() => setAuthTab("register")} className="text-[#DC2626] font-semibold cursor-pointer hover:underline">
                                        Create one free
                                    </span>
                                </p>
                            </>
                        ) : (
                            <>
                                <h2 className="text-[#1E3A8A] text-2xl font-extrabold mb-1.5">Start publishing</h2>
                                <p className="text-gray-500 text-[13px] mb-7">Free to join. No credit card needed.</p>

                                {regError && (
                                    <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3.5 py-2.5 mb-4 text-[#DC2626] text-xs">
                                        {regError}
                                    </div>
                                )}
                                {regSuccess && (
                                    <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg px-3.5 py-2.5 mb-4 text-[#15803D] text-xs font-semibold">
                                        ✓ Account created! Redirecting to sign in…
                                    </div>
                                )}

                                <form onSubmit={handleRegister} className="flex flex-col gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 mb-1.5 tracking-wider">Full name</label>
                                        <input type="text" value={regName} onChange={e => setRegName(e.target.value)}
                                            placeholder="Your name" className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-200 focus:border-[#1E3A8A] rounded-lg text-sm text-gray-900 outline-none transition-colors bg-[#F9FAFB]"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 mb-1.5 tracking-wider">Email address</label>
                                        <input type="email" value={regEmail} onChange={e => setRegEmail(e.target.value)}
                                            placeholder="you@example.com" required className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-200 focus:border-[#1E3A8A] rounded-lg text-sm text-gray-900 outline-none transition-colors bg-[#F9FAFB]"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 mb-1.5 tracking-wider">Password</label>
                                        <input type="password" value={regPassword} onChange={e => setRegPassword(e.target.value)}
                                            placeholder="Min 6 characters" required className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-200 focus:border-[#1E3A8A] rounded-lg text-sm text-gray-900 outline-none transition-colors bg-[#F9FAFB]"
                                        />
                                    </div>
                                    <button type="submit" disabled={regLoading} className={`w-full py-3.5 text-white rounded-xl text-sm font-bold tracking-wider transition-all duration-150 cursor-pointer ${regLoading ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-[#DC2626] to-[#B91C1C] hover:opacity-95"}`}>
                                        {regLoading ? "Creating account…" : "Create Free Account →"}
                                    </button>
                                </form>

                                <div className="flex items-center gap-2 my-4">
                                    <div className="flex-1 h-[1px] bg-gray-200" />
                                    <span className="text-[11px] text-gray-400 uppercase font-bold">Or</span>
                                    <div className="flex-1 h-[1px] bg-gray-200" />
                                </div>
                                <button
                                    type="button"
                                    onClick={handleGoogleLogin}
                                    className="w-full flex items-center justify-center gap-2.5 border border-gray-300 rounded-lg py-2.5 px-4 bg-white hover:bg-gray-50 text-gray-700 text-xs font-semibold cursor-pointer transition-colors"
                                >
                                    <svg width="18" height="18" viewBox="0 0 18 18">
                                        <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4" />
                                        <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853" />
                                        <path d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.96H.957C.347 6.173 0 7.549 0 9s.347 2.827.957 4.04l3.007-2.333z" fill="#FBBC05" />
                                        <path d="M9 3.58c1.32 0 2.507.454 3.44 1.347l2.58-2.58C13.463.89 11.426 0 9 0 5.482 0 2.438 2.017.957 4.96L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335" />
                                    </svg>
                                    Sign up with Google
                                </button>

                                <p className="text-center mt-5 text-[13px] text-gray-500">
                                    Already have an account?{" "}
                                    <span onClick={() => setAuthTab("login")} className="text-[#DC2626] font-semibold cursor-pointer hover:underline">
                                        Sign in
                                    </span>
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </section>

            {/* ── FEATURES ─────────────────────────────────────────────────── */}
            <section id="features" className="py-24 px-6 md:px-10 bg-white">
                <div className="max-w-[1100px] mx-auto">
                    <div className="text-center mb-16">
                        <span className="block text-[11px] font-bold tracking-widest uppercase text-[#DC2626] mb-3">What We Offer</span>
                        <h2 className="text-4xl font-extrabold text-[#1E3A8A] mb-4 tracking-tight">Everything an author needs</h2>
                        <p className="text-gray-500 max-w-[520px] mx-auto text-base leading-relaxed">
                            From your first draft to your first sale — MridaPub handles the publishing pipeline so you can focus on writing.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-7">
                        {features.map(({ icon, title, desc }) => (
                            <div key={title} className="bg-white border border-gray-200 rounded-2xl p-7 shadow-[0_2px_12px_rgba(0,0,0,0.06)] border-t-[3px] border-t-[#DC2626] transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(30,58,138,0.12)]"
                            >
                                <div className="text-4xl mb-4">{icon}</div>
                                <h3 className="text-[#1E3A8A] text-lg font-bold mb-2.5">{title}</h3>
                                <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── HOW IT WORKS ─────────────────────────────────────────────── */}
            <section id="how-it-works" className="py-24 px-6 md:px-10 bg-[#F8F7F4]">
                <div className="max-w-[900px] mx-auto text-center">
                    <span className="block text-[11px] font-bold tracking-widest uppercase text-[#DC2626] mb-3">The Process</span>
                    <h2 className="text-4xl font-extrabold text-[#1E3A8A] mb-4 tracking-tight">Publish in three steps</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-14 text-left">
                        {[
                            { step: "01", title: "Write & Upload", body: "Submit your manuscript in Word or PDF format. Our system processes it instantly." },
                            { step: "02", title: "Design & Review", body: "Pick or generate a cover, review your formatted book, and approve the final proof." },
                            { step: "03", title: "Publish & Earn", body: "Go live across ebook and print platforms. Track royalties in real time." },
                        ].map(({ step, title, body }) => (
                            <div key={step} className="relative pl-0">
                                <div className="text-5xl font-extrabold text-[#EEF2FF] leading-none mb-2">{step}</div>
                                <h3 className="text-[#1E3A8A] text-lg font-bold mb-2">{title}</h3>
                                <p className="text-gray-500 text-sm leading-relaxed">{body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── CONTACT ──────────────────────────────────────────────────── */}
            <section id="contact" className="py-24 px-6 md:px-10 bg-[#1E3A8A]">
                <div className="max-w-[1100px] mx-auto flex flex-col lg:flex-row gap-16 lg:gap-20 items-start">
                    {/* Left info */}
                    <div className="flex-1 min-w-[280px]">
                        <span className="block text-[11px] font-bold tracking-widest uppercase text-[#FCA5A5] mb-3">Get In Touch</span>
                        <h2 className="text-white text-4xl font-extrabold leading-tight mb-5 tracking-tight">
                            Ready to publish?<br />Let's talk.
                        </h2>
                        <p className="text-white/65 text-[15px] leading-relaxed mb-10">
                            Our publishing consultants are happy to guide you through the process — whether you're submitting your first manuscript or your tenth.
                        </p>
                        <div className="flex flex-col gap-5">
                            {[
                                { icon: "📧", label: "Email", value: "publish@mridapub.in" },
                                { icon: "📞", label: "Phone", value: "+91 11 45 661 661" },
                                { icon: "📍", label: "Office", value: "C-3, S. F. , Subhash Park, Uttam Nagar, New Delhi 110059" },
                            ].map(({ icon, label, value }) => (
                                <div key={label} className="flex items-center gap-4">
                                    <div className="w-11 h-11 bg-[#DC2626]/15 rounded-xl flex items-center justify-center text-xl shrink-0">{icon}</div>
                                    <div>
                                        <div className="text-white/45 text-[11px] font-bold tracking-widest uppercase">{label}</div>
                                        <div className="text-white text-sm font-medium">{value}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right form */}
                    <div className="flex-1 min-w-[320px] w-full bg-white rounded-2xl p-8 md:p-10 shadow-[0_24px_60px_rgba(0,0,0,0.3)]">
                        <h3 className="text-[#1E3A8A] text-xl font-extrabold mb-1.5">Send us a message</h3>
                        <p className="text-gray-400 text-xs mb-7">We'll get back to you within 24 hours.</p>

                        {contactSuccess ? (
                            <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl p-7 text-center">
                                <div className="text-4xl mb-3">✅</div>
                                <p className="text-[#15803D] font-bold text-base">Message sent!</p>
                                <p className="text-gray-500 text-sm mt-1.5">We'll be in touch soon.</p>
                                <button onClick={() => setContactSuccess(false)} className="mt-4 text-[#DC2626] bg-transparent border-none cursor-pointer font-semibold text-xs">Send another →</button>
                            </div>
                        ) : (
                            <form onSubmit={handleContact} className="flex flex-col gap-4">
                                {contactError && (
                                    <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3.5 py-2.5 text-[#DC2626] text-xs">{contactError}</div>
                                )}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 mb-1.5 tracking-wider">Full name *</label>
                                        <input type="text" value={contact.name} onChange={e => setContact({ ...contact, name: e.target.value })} placeholder="Your name" required className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-200 focus:border-[#1E3A8A] rounded-lg text-sm text-gray-900 outline-none transition-colors bg-[#F9FAFB]" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 mb-1.5 tracking-wider">Phone</label>
                                        <input type="tel" value={contact.phone} onChange={e => setContact({ ...contact, phone: e.target.value })} placeholder="+91 00000 00000" className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-200 focus:border-[#1E3A8A] rounded-lg text-sm text-gray-900 outline-none transition-colors bg-[#F9FAFB]" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1.5 tracking-wider">Email address *</label>
                                    <input type="email" value={contact.email} onChange={e => setContact({ ...contact, email: e.target.value })} placeholder="you@example.com" required className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-200 focus:border-[#1E3A8A] rounded-lg text-sm text-gray-900 outline-none transition-colors bg-[#F9FAFB]" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1.5 tracking-wider">Message *</label>
                                    <textarea value={contact.message} onChange={e => setContact({ ...contact, message: e.target.value })} placeholder="Tell us about your book or publishing goals…" required rows={4}
                                        className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-200 focus:border-[#1E3A8A] rounded-lg text-sm text-gray-900 outline-none transition-colors bg-[#F9FAFB] resize-y leading-relaxed" />
                                </div>
                                <button type="submit" disabled={contactLoading} className={`w-full py-3.5 text-white rounded-xl text-sm font-bold tracking-wider transition-all duration-150 cursor-pointer ${contactLoading ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] hover:opacity-95"}`}>
                                    {contactLoading ? "Sending…" : "Send Message →"}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>

            {/* ── FOOTER ───────────────────────────────────────────────────── */}
            <footer className="bg-[#111827] py-7 px-6 md:px-10 flex flex-col sm:flex-row justify-between items-center gap-3">
                <span className="text-white font-extrabold text-base">
                    Mrida<span className="text-[#DC2626]">Pub</span>
                </span>
                <span className="text-gray-500 text-xs text-center sm:text-left">© 2026 MridaPub (Akshar Consultancy Services Pvt. Ltd.). All rights reserved.</span>
                {/* Forgot Password / OTP Verification Modal Overlay */}
                {showForgotModal && (
                    <div className="fixed inset-0 bg-[#0F172A]/65 backdrop-blur-sm flex items-center justify-center z-[10000] p-5" onClick={() => setShowForgotModal(false)}>
                        <div className="bg-white rounded-3xl w-full max-w-[460px] shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] overflow-hidden border border-[#E2E8F0]" onClick={e => e.stopPropagation()}>

                            {/* Header */}
                            <div className="py-5 px-6 border-b border-[#F1F5F9] flex justify-between items-center bg-[#F8FAFC]">
                                <h3 className="text-lg font-extrabold text-[#0F172A] m-0">
                                    Reset Password
                                </h3>
                                <button
                                    onClick={() => setShowForgotModal(false)}
                                    className="bg-transparent border-none text-xl text-[#64748B] cursor-pointer p-1 hover:text-[#0F172A] transition-colors"
                                >
                                    ✕
                                </button>
                            </div>

                            {/* Body */}
                            <div className="p-7">
                                {forgotError && (
                                    <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3.5 py-2.5 mb-4 text-[#DC2626] text-xs">
                                        {forgotError}
                                    </div>
                                )}
                                {forgotSuccess ? (
                                    <div className="text-center py-3">
                                        <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl p-4 mb-4 text-[#15803D] text-sm font-semibold leading-relaxed">
                                            ✓ Link Sent
                                            <p className="font-normal m-2 text-[#166534]">{forgotSuccess}</p>
                                        </div>
                                        <button
                                            onClick={() => setShowForgotModal(false)}
                                            className="w-full py-3.5 text-white rounded-xl text-sm font-bold tracking-wider transition-all duration-150 cursor-pointer bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF]"
                                        >
                                            Close Window
                                        </button>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSendResetLink} className="flex flex-col gap-4">
                                        <p className="text-gray-500 text-xs mb-2 leading-relaxed">
                                            Enter your email below. We will send a secure password reset link to your email address to reset your password.
                                        </p>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-700 mb-1.5 tracking-wider">Recovery Email Address</label>
                                            <input
                                                type="email"
                                                required
                                                value={forgotEmail}
                                                onChange={e => setForgotEmail(e.target.value)}
                                                placeholder="you@example.com"
                                                className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-200 focus:border-[#1E3A8A] rounded-lg text-sm text-gray-900 outline-none transition-colors bg-[#F9FAFB]"
                                                disabled={forgotLoading}
                                            />
                                        </div>
                                        <button type="submit" disabled={forgotLoading} className={`w-full py-3.5 text-white rounded-xl text-sm font-bold tracking-wider transition-all duration-150 cursor-pointer ${forgotLoading ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] hover:opacity-95"}`}>
                                            {forgotLoading ? "Sending Link…" : "Send Reset Link →"}
                                        </button>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </footer>
        </div>
    );
};

/* ── Shared style tokens (Commented out - migrated to Tailwind CSS) ────────
const labelStyle = {
    display: "block",
    fontSize: "12px",
    fontWeight: "700",
    color: "#374151",
    marginBottom: "6px",
    letterSpacing: "0.03em",
};

const inputStyle = {
    width: "100%",
    padding: "11px 14px",
    border: "1.5px solid #E5E7EB",
    borderRadius: "8px",
    fontSize: "14px",
    color: "#111827",
    outline: "none",
    transition: "border-color 0.15s",
    boxSizing: "border-box",
    background: "#F9FAFB",
    fontFamily: "inherit",
};

const primaryBtnStyle = (disabled) => ({
    width: "100%",
    padding: "13px",
    background: disabled ? "#9CA3AF" : "linear-gradient(135deg, #1E3A8A, #1E40AF)",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "700",
    cursor: disabled ? "not-allowed" : "pointer",
    letterSpacing: "0.03em",
    transition: "opacity 0.15s",
    fontFamily: "inherit",
});

const eyebrowStyle = {
    display: "block",
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "0.15em",
    textTransform: "uppercase",
    color: "#DC2626",
    marginBottom: "12px",
};

const sectionHeadingStyle = {
    fontSize: "36px",
    fontWeight: "800",
    color: "#1E3A8A",
    marginBottom: "16px",
    letterSpacing: "-0.02em",
};

const featureCardStyle = {
    background: "#fff",
    border: "1px solid #E5E7EB",
    borderRadius: "16px",
    padding: "28px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    transition: "all 0.2s ease",
    borderTop: "3px solid #DC2626",
};
*/

export default LandingPage;